"use client";

import { useCallback } from "react";

import { useRouter } from "next/navigation";

import { BrandSuggestionCard } from "@/components/brands/BrandSuggestionCard";
import { TextArrowButton } from "@/components/shared/TextArrowButton";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { buildCanonicalBrandPath } from "@/lib/brand-routing";
import { appendReturnTo, BRAND_EXPLORER_RESULTS_PATH } from "@/lib/route-paths";
import {
  type BrandIndexSearchResult,
  type PublishedCuratedSearchInfo,
} from "@/lib/shop-agent-client";

const DISPLAY_LIMIT = 8;

interface CuratedBrandRailProps {
  query: PublishedCuratedSearchInfo;
  brands: BrandIndexSearchResult[];
}

export function CuratedBrandRail({ query, brands }: CuratedBrandRailProps) {
  const router = useRouter();
  const displayBrands = brands.slice(0, DISPLAY_LIMIT);

  const navigateToBrand = useCallback(
    (brandName: string, brandSlug?: string | null) => {
      router.push(buildCanonicalBrandPath({ brandName, brandSlug: brandSlug ?? undefined }));
    },
    [router]
  );

  const navigateToAll = useCallback(() => {
    const currentPath = `${window.location.pathname}${window.location.search || ""}`;
    const pathWithReturnTo = appendReturnTo(BRAND_EXPLORER_RESULTS_PATH, currentPath);
    router.push(
      `${pathWithReturnTo}${pathWithReturnTo.includes("?") ? "&" : "?"}q=${encodeURIComponent(query.title)}`
    );
  }, [query.title, router]);

  const { scrollRef, canScrollLeft, canScrollRight, scrollLeft, scrollRight } = useHorizontalScroll(
    {
      threshold: 1,
      scrollAmount: "viewport",
      viewportMultiplier: 0.75,
      deps: [displayBrands.length],
    }
  );

  if (displayBrands.length === 0) return null;

  return (
    <section className="w-full px-6 lg:px-8">
      <div className="mb-4 flex items-center justify-between">
        <button
          className="text-xl font-medium text-gray-900 transition-opacity hover:opacity-70"
          onClick={navigateToAll}
        >
          {query.title}
        </button>
        <TextArrowButton label="See all" onClick={navigateToAll} />
      </div>
      <div className="relative">
        {canScrollLeft && (
          <button
            className="absolute -left-4 top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full border bg-white shadow"
            onClick={scrollLeft}
            aria-label="Previous brands"
          >
            Prev
          </button>
        )}
        {canScrollRight && (
          <button
            className="absolute -right-4 top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full border bg-white shadow"
            onClick={scrollRight}
            aria-label="Next brands"
          >
            Next
          </button>
        )}
        <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-4">
          {displayBrands.map((brand, index) => (
            <div
              key={brand.brand_uuid ?? `${brand.brand_name}-${index}`}
              className="w-96 flex-shrink-0"
            >
              <BrandSuggestionCard
                brandName={brand.brand_name || "Unknown brand"}
                subheading={brand.directory_subheading ?? brand.brand_description ?? undefined}
                onNavigate={() => navigateToBrand(brand.brand_name || "", brand.brand_slug)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
